import { AxiosResponse, AxiosInstance, AxiosError } from 'axios';
import { QueryKeys } from '../enums/QueryKeys';
import { useMutation } from '@tanstack/react-query';
import { MutationOptions, MutationResult, UnlinkDeviceResult } from '@thingslog/repositories';

export interface LinkDeviceQueryClient {
  linkDevice: (deviceNumber: string, companyId: number) => Promise<void>;
  useLinkDevice: (
    options?: MutationOptions<void, LinkDeviceVariables>
  ) => MutationResult<void, LinkDeviceVariables>;
  unlinkDevice: (
    deviceNumber: string,
    companyId: number,
    removeForSubAccounts: boolean
  ) => Promise<UnlinkDeviceResult>;
  useUnlinkDevice: (
    options?: MutationOptions<UnlinkDeviceResult, UnlinkDeviceVariables>
  ) => MutationResult<UnlinkDeviceResult, UnlinkDeviceVariables>;
}

export function createLinkDeviceQueryClient(axios: AxiosInstance): LinkDeviceQueryClient {
  return new LinkDeviceQueryImp(axios);
}

class LinkDeviceQueryImp {
  public constructor(private axios: AxiosInstance) {}

  public linkDevice = async (deviceNumber: string, companyId: number): Promise<void> => {
    return await this.axios
      .post(`/api/link_devices/${deviceNumber}/${companyId}`)
      .then((response: AxiosResponse<void>) => {
        return response.data;
      })
      .catch((error: AxiosError) => {
        throw error;
      });
  };

  public useLinkDevice = (
    options?: MutationOptions<void, LinkDeviceVariables>
  ): MutationResult<void, LinkDeviceVariables> => {
    return useMutation<void, AxiosError, LinkDeviceVariables>(
      [QueryKeys.LinkDevice],
      (params: LinkDeviceVariables) => this.linkDevice(params.deviceNumber, params.companyId),
      options
    );
  };

  public unlinkDevice = async (
    deviceNumber: string,
    companyId: number,
    removeForSubAccounts: boolean
  ): Promise<UnlinkDeviceResult> => {
    return await this.axios
      .delete(`/api/link_devices/${deviceNumber}/${companyId}`, {
        params: {
          removeForSubAccounts: removeForSubAccounts,
        },
      })
      .then((response: AxiosResponse<UnlinkDeviceResult>) => {
        return response.data;
      })
      .catch((error: AxiosError) => {
        throw error;
      });
  };

  public useUnlinkDevice = (
    options?: MutationOptions<UnlinkDeviceResult, UnlinkDeviceVariables>
  ): MutationResult<UnlinkDeviceResult, UnlinkDeviceVariables> => {
    return useMutation<UnlinkDeviceResult, AxiosError, UnlinkDeviceVariables>(
      [QueryKeys.UnlinkDevice],
      (params: UnlinkDeviceVariables) =>
        this.unlinkDevice(params.deviceNumber, params.companyId, params.removeForSubAccounts),
      options
    );
  };
}

interface LinkDeviceVariables {
  deviceNumber: string;
  companyId: number;
}

interface UnlinkDeviceVariables {
  deviceNumber: string;
  companyId: number;
  removeForSubAccounts: boolean;
}
